import { of, Subject, forkJoin } from 'rxjs';
import * as i0 from '@angular/core';
import { Injectable } from '@angular/core';
import { retryWhen, mergeMap, delay, shareReplay, map, take, tap } from 'rxjs/operators';
import * as i1$1 from '@ft-alts/ui-components/src/lib/shared-lib';
import { Logger } from '@ft-alts/ui-components/src/lib/shared-lib';
import * as i1 from 'apollo-angular';
import gql from 'graphql-tag';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import * as i7 from '@angular/common/http';

const logger$2 = Logger.getLogger('GraphQLDataService');
var ProductTypeParameter;
(function (ProductTypeParameter) {
    ProductTypeParameter["MUTUAL_FUNDS"] = "mf";
    ProductTypeParameter["CLOSED_END_FUNDS"] = "cef";
    ProductTypeParameter["VARIABLE_INSURANCES"] = "variable-insurance";
    ProductTypeParameter["MONEY_MARKET_FUNDS"] = "mmf";
    ProductTypeParameter["PORTFOLIOS_529"] = "529";
    ProductTypeParameter["ETFS"] = "etf";
    ProductTypeParameter["TIFs"] = "tif";
    ProductTypeParameter["SMAs"] = "sma";
    ProductTypeParameter["INTERVAL_FUNDS"] = "if";
    ProductTypeParameter["TENDER_OFFER_FUNDS"] = "tof";
})(ProductTypeParameter || (ProductTypeParameter = {}));
class GraphQLDataService {
    constructor(
    // protected siteConfigService: SiteConfigService (currently site config is in the app - we may need to reconsider this)
    apollo) {
        this.apollo = apollo;
    }
    register(gqlQuery, variablesObj = {}) {
        return this.apollo
            .query({
            query: gqlQuery,
            variables: variablesObj,
            fetchPolicy: 'no-cache',
        })
            .pipe(
        // in case of error 0 or 500 from the server we retry
        // TODO this needs to be refactored when we do Angular Upgrade
        // DEPRECATED retryWhen is deprecated and needs to be replaced with retry with RetryConfig (new parameters since ver 9)
        retryWhen((error) => {
            return error.pipe(mergeMap((lastError, index) => {
                var _a, _b;
                if (index < 1 &&
                    (((_a = lastError === null || lastError === void 0 ? void 0 : lastError.networkError) === null || _a === void 0 ? void 0 : _a.status) === 0 ||
                        ((_b = lastError === null || lastError === void 0 ? void 0 : lastError.networkError) === null || _b === void 0 ? void 0 : _b.status) === 500)) {
                    logger$2.debug(index + ' - inside retry GraphQL ...');
                    return of(lastError).pipe(delay(500));
                }
                throw lastError;
            }));
        }), shareReplay(1));
    }
    getCountry() {
        // Single site
        return 'US';
        // return this.siteConfigService.getPdsCountry();
    }
    getLanguage() {
        return 'en_US';
        // return this.siteConfigService.getPdsLanguage();
    }
    getDateFormat() {
        return 'MM/dd/yyyy';
        // return this.siteConfigService.common.dateFormat;
    }
}
GraphQLDataService.ɵfac = function GraphQLDataService_Factory(t) { return new (t || GraphQLDataService)(i0.ɵɵinject(i1.Apollo)); };
GraphQLDataService.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: GraphQLDataService, factory: GraphQLDataService.ɵfac, providedIn: 'root' });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(GraphQLDataService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: i1.Apollo }]; }, null); })();

const LabelsQuery = gql `
  query Labels($countrycode: String!, $languagecode: String!) {
    Labels(countrycode: $countrycode, languagecode: $languagecode) {
      pdssrvcfieldname
      transvalue
    }
  }
`;

const logger$1 = Logger.getLogger('LabelLoader');
class LabelLoader {
    constructor(translateService) {
        this.translateService = translateService;
    }
    /**
     * To set labels for translation
     * Extract the resourcebundles for labels from models Object
     * and pass it to setTranslation method of NGX-Translate with namespaces
     * @param models ResourceBundleModels Object
     */
    setLabelTranslation(models) {
        // check to see if they are already loaded.
        if (!this.translateService.isLoaded()) {
            const commonLabels = models['ft.core.common'];
            const altsLabels = models['ft.core.ftAlts'];
            const translateLabels = {
                common: commonLabels,
                ftAlts: altsLabels,
            };
            this.addLabels(translateLabels);
        }
    }
    loadPdsTranslations(pdsLabels) {
        this.addLabels({ pds: pdsLabels }, false);
        logger$1.debug('PDS Labels loaded');
    }
    addLabels(labels, ignoreOverrides = true) {
        // if showKeys then let MissingLabelHandler kick in.
        if (!LabelLoader.showKeys) {
            this.translateService.setTranslation(labels, ignoreOverrides);
        }
    }
}
LabelLoader.showKeys = false;
LabelLoader.ɵfac = function LabelLoader_Factory(t) { return new (t || LabelLoader)(i0.ɵɵinject(i1$1.TranslateService)); };
LabelLoader.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: LabelLoader, factory: LabelLoader.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(LabelLoader, [{
        type: Injectable
    }], function () { return [{ type: i1$1.TranslateService }]; }, null); })();

class LabelsService extends GraphQLDataService {
    constructor(apollo, labelLoader) {
        super(apollo);
        this.labelLoader = labelLoader;
    }
    loadPDSLabels$() {
        return this.register(LabelsQuery, {
            countrycode: this.getCountry(),
            languagecode: this.getLanguage(),
        }).pipe(map((data) => {
            const pdsLabels = data.data.Labels;
            const translated = {};
            pdsLabels.forEach((label) => {
                translated[`${label.pdssrvcfieldname}`] = label.transvalue;
            });
            this.labelLoader.loadPdsTranslations(translated);
            return true;
        }), take(1));
    }
}
LabelsService.ɵfac = function LabelsService_Factory(t) { return new (t || LabelsService)(i0.ɵɵinject(i1.Apollo), i0.ɵɵinject(LabelLoader)); };
LabelsService.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: LabelsService, factory: LabelsService.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(LabelsService, [{
        type: Injectable
    }], function () { return [{ type: i1.Apollo }, { type: LabelLoader }]; }, null); })();

class Mapper {
    constructor(mapperParams, mapperFactory) {
        this.mapperParams = mapperParams;
        this.mapperFactory = mapperFactory;
    }
    createMapper(mapper) {
        return this.mapperFactory.createMapper(mapper, this.mapperParams);
    }
}

const geographicAllocationColors = [
    { color1: '#AD3BD3', color2: '#C75EED' },
    { color1: '#008A81', color2: '#01C0B4' },
    { color1: '#386AFF' },
    { color1: '#00BFB2', color2: '#009C92' },
];
const propertyColors = [
    { color1: '#00BFB2', color2: '#009C92' },
    { color1: '#3769FF', color2: '#0A3CD2' },
    { color1: '#AD3BD3', color2: '#C75EED' },
    { color1: '#8C8C8C', color2: '#BFBFBF' },
    { color1: '#6730E3', color2: '#8760EF' },
    { color1: '#72DBD5', color2: '#29BDB5' },
];
const assetAllocationColors = [
    { color1: '#00BFB2', color2: '#009C92' },
    { color1: '#3769FF', color2: '#0A3CD2' },
    { color1: '#AD3BD3', color2: '#C75EED' },
];
class PortfolioMapperCPREX extends Mapper {
    constructor(mapperParams, mapperFactory) {
        super(mapperParams, mapperFactory);
    }
    toDomain(portfolioDTO) {
        var _a, _b, _c, _d, _e;
        const assetAllocation = [];
        const geographicAllocation = [];
        const sectorAllocation = [];
        (_a = portfolioDTO === null || portfolioDTO === void 0 ? void 0 : portfolioDTO.assetallocation) === null || _a === void 0 ? void 0 : _a.forEach((val, i) => {
            var _a, _b;
            const data = {
                name: val.assetalloccatg,
                description: val.asofdate,
                value: val.brkdwnpct,
                color1: (_a = assetAllocationColors[i]) === null || _a === void 0 ? void 0 : _a.color1,
                color2: (_b = assetAllocationColors[i]) === null || _b === void 0 ? void 0 : _b.color2,
            };
            assetAllocation.push(data);
        });
        (_b = portfolioDTO === null || portfolioDTO === void 0 ? void 0 : portfolioDTO.countryallocation) === null || _b === void 0 ? void 0 : _b.forEach((val, i) => {
            var _a, _b;
            const data = {
                name: val.brkdwnname,
                description: val.asofdate,
                value: val.brkdwnpct,
                color1: (_a = geographicAllocationColors[i]) === null || _a === void 0 ? void 0 : _a.color1,
                color2: (_b = geographicAllocationColors[i]) === null || _b === void 0 ? void 0 : _b.color2,
            };
            geographicAllocation.push(data);
        });
        (_c = portfolioDTO === null || portfolioDTO === void 0 ? void 0 : portfolioDTO.sectorallocation) === null || _c === void 0 ? void 0 : _c.forEach((val, i) => {
            var _a, _b;
            const data = {
                name: val.sectorname,
                description: val.asofdate,
                label: val.sectorname.toLowerCase().replace(" ", "-"),
                value: val.brkdwnpct,
                color1: (_a = propertyColors[i]) === null || _a === void 0 ? void 0 : _a.color1,
                color2: (_b = propertyColors[i]) === null || _b === void 0 ? void 0 : _b.color2,
            };
            sectorAllocation.push(data);
        });
        return {
            asOfDate: ((_d = portfolioDTO === null || portfolioDTO === void 0 ? void 0 : portfolioDTO.assetallocation) === null || _d === void 0 ? void 0 : _d.length) > 0 ? (_e = portfolioDTO === null || portfolioDTO === void 0 ? void 0 : portfolioDTO.assetallocation[0]) === null || _e === void 0 ? void 0 : _e.asofdate : null,
            assetAllocation: { chartData: assetAllocation },
            geographicAllocation: { chartData: geographicAllocation.reverse() },
            propertyType: { chartData: sectorAllocation },
        };
    }
}

class PortfolioMapperBSP extends Mapper {
    constructor(mapperParams, mapperFactory) {
        super(mapperParams, mapperFactory);
    }
    toDomain(portfolioDTO) {
        var _a, _b, _c;
        let assetAllocation = [];
        let assetAllocationCategories = [];
        let industryAllocation = [];
        let industryAllocationCategories = [];
        let securityAllocation = [];
        let securityAllocationCategories = [];
        let industryAllocationFull = [];
        let assetAllocationFull = [];
        let securityAllocationFull = [];
        (_a = portfolioDTO === null || portfolioDTO === void 0 ? void 0 : portfolioDTO.assetallocation) === null || _a === void 0 ? void 0 : _a.forEach((val, i) => {
            let data = {
                name: val.brkdwnpct + "%",
                y: Number.parseFloat(val.brkdwnpct),
                category: val.assetalloccatg
            };
            assetAllocationFull.push(data);
        });
        (_b = portfolioDTO === null || portfolioDTO === void 0 ? void 0 : portfolioDTO.sectorallocation) === null || _b === void 0 ? void 0 : _b.forEach((val) => {
            let data = {
                name: val.brkdwnpct + "%",
                y: Number.parseFloat(val.brkdwnpct),
                category: val.sectorname
            };
            if (val.allocheadinglocal === "Industry Allocation") {
                industryAllocationFull.push(data);
            }
            else {
                securityAllocationFull.push(data);
            }
        });
        let sortedAssetAllocation = this.sortArrayDesc(assetAllocationFull);
        let sortedSecurityAllocation = this.sortArrayDesc(securityAllocationFull);
        let sortedIndustryAllocation = this.sortArrayDesc(industryAllocationFull);
        let industryAllocationWithOther = [];
        if (sortedIndustryAllocation.length > 9) {
            let trunctatedIndustryAllocation = sortedIndustryAllocation.slice(0, 9);
            let otherTotal = 0;
            let otherElements = sortedIndustryAllocation.slice(9);
            otherElements.forEach(element => {
                otherTotal += Number.parseFloat(element.y);
            });
            trunctatedIndustryAllocation.forEach(element => {
                industryAllocationWithOther.push(element);
            });
            industryAllocationWithOther.push({ name: otherTotal + "%", y: otherTotal, category: "Other" });
        }
        sortedAssetAllocation.forEach((val) => {
            assetAllocationCategories.push(val.category);
            assetAllocation.push({ name: val.name, y: val.y });
        });
        sortedSecurityAllocation.forEach((val) => {
            securityAllocationCategories.push(val.category);
            securityAllocation.push({ name: val.name, y: val.y });
        });
        industryAllocationWithOther.forEach((val) => {
            industryAllocationCategories.push(val.category);
            industryAllocation.push({ name: val.name, y: val.y });
        });
        return {
            asOfDate: ((_c = portfolioDTO === null || portfolioDTO === void 0 ? void 0 : portfolioDTO.assetallocation) === null || _c === void 0 ? void 0 : _c.length) ? portfolioDTO === null || portfolioDTO === void 0 ? void 0 : portfolioDTO.assetallocation[0].asofdate : null,
            assetAllocation: { categories: assetAllocationCategories, yAxis: assetAllocation },
            securityAllocation: { categories: securityAllocationCategories, yAxis: securityAllocation },
            industryAllocation: { categories: industryAllocationCategories, yAxis: industryAllocation }
        };
    }
    sortArrayDesc(arrayToBeSorted) {
        return arrayToBeSorted.sort((a, b) => {
            if (a.y > b.y) {
                return -1;
            }
            else if (b.y > a.y) {
                return 1;
            }
            return 0;
        });
    }
}

const hedgeManagerAllocationTableOrder = ['Relative Value', 'Global Macro', 'Long Short Equity', 'Event Driven'];
class PortfolioMapperK2 extends Mapper {
    constructor(mapperParams, mapperFactory) {
        super(mapperParams, mapperFactory);
    }
    toDomain(portfolioDTO) {
        let assetAllocationCategories = [];
        let assetAllocationNetValues = [];
        let assetAllocationGrossValues = [];
        let geographyCategories = [];
        let geographyNetValues = [];
        let geographyGrossValues = [];
        let hedgeManagerAllocationHeaders = [];
        let hedgeManagerAllocationData = [];
        let hedgeManagerChartData = [];
        let securityExposureCategories = [];
        let securityExposureNetValues = [];
        let securityExposureGrossValues = [];
        this.generateAssetAllocationData(portfolioDTO, assetAllocationCategories, assetAllocationGrossValues, assetAllocationNetValues);
        this.generateGeographyData(portfolioDTO, geographyCategories, geographyGrossValues, geographyNetValues);
        this.generateHedgeManagerAllocationData(portfolioDTO, hedgeManagerAllocationHeaders, hedgeManagerAllocationData, hedgeManagerChartData);
        this.generateSecurityExposureData(portfolioDTO, securityExposureCategories, securityExposureGrossValues, securityExposureNetValues);
        let holdingsPositiveHeaders = [];
        let holdingsNegativeHeaders = [];
        let holdingsPositiveValues = [];
        let holdingsNegativeValues = [];
        let holdingsFullPositive = [];
        let holdingsFullNegative = [];
        this.generateInitialHoldings(portfolioDTO, holdingsFullPositive, holdingsFullNegative);
        holdingsFullPositive = this.sortPositiveHoldingsDesc(holdingsFullPositive);
        holdingsFullNegative = this.sortNegativeHoldingsASC(holdingsFullNegative);
        this.formatHoldingsData(holdingsFullPositive, holdingsPositiveHeaders, holdingsPositiveValues, holdingsFullNegative, holdingsNegativeHeaders, holdingsNegativeValues);
        let strategyDates = [];
        let strategyEventDrivenValues = [];
        let strategyLongShortEquityValues = [];
        let strategyGlobalMacroValues = [];
        let strategyRelativeValueValues = [];
        this.generateStrategyData(portfolioDTO, strategyEventDrivenValues, strategyLongShortEquityValues, strategyGlobalMacroValues, strategyRelativeValueValues, strategyDates);
        let portfolioGrossExposureValues = [];
        let portfolioLongExposureValues = [];
        let portfolioNetExposureValues = [];
        let portfolioShortExposureValues = [];
        this.generatePortfolioExposureData(portfolioDTO, portfolioGrossExposureValues, portfolioLongExposureValues, portfolioNetExposureValues, portfolioShortExposureValues);
        return {
            asOfDate: portfolioDTO.assetallocation[0].asofdate,
            assetsExposure: {
                frequency: portfolioDTO.assetallocation[0].frequency,
                categories: Array.from(assetAllocationCategories),
                chartData: [
                    { name: 'Gross Exposure', data: assetAllocationGrossValues, anchor: '17' },
                    { name: 'Net Exposure', data: assetAllocationNetValues, anchor: '19' },
                ],
            },
            manager: {
                frequency: portfolioDTO.hedgemanagerallocation[0].frequency,
                chartData: hedgeManagerChartData.sort((a, b) => hedgeManagerAllocationTableOrder.indexOf(a.header.name) - hedgeManagerAllocationTableOrder.indexOf(b.header.name)),
            },
            geography: {
                frequency: portfolioDTO.countryallocation[0].frequency,
                categories: Array.from(geographyCategories),
                chartData: [
                    { name: 'Gross Exposure', data: geographyGrossValues, anchor: '17' },
                    { name: 'Net Exposure', data: geographyNetValues, anchor: '19' },
                ],
            },
            securityExposure: {
                frequency: portfolioDTO.securityexposure[0].frequency,
                categories: Array.from(securityExposureCategories),
                chartData: [
                    { name: 'Gross Exposure', data: securityExposureGrossValues, anchor: '17' },
                    { name: 'Net Exposure', data: securityExposureNetValues, anchor: '19' },
                ],
            },
            topLongShortExposures: [
                { categories: holdingsPositiveHeaders, yAxis: holdingsPositiveValues, frequency: portfolioDTO.topholdings[0].frequency },
                { categories: holdingsNegativeHeaders, yAxis: holdingsNegativeValues, frequency: portfolioDTO.topholdings[0].frequency },
            ],
            portfolioExposure: {
                frequency: portfolioDTO.exposureallocation[0].frequency,
                chartData: [
                    ['Gross Exposure', ...portfolioGrossExposureValues, '17'],
                    ['Long Exposure', ...portfolioLongExposureValues, '18'],
                    ['Net Exposure', ...portfolioNetExposureValues, '19'],
                    ['Short Exposure', ...portfolioShortExposureValues, '20'],
                ],
            },
            strategy: {
                frequency: portfolioDTO.hedgeallocationhistory[0].frequency,
                categories: [...strategyDates],
                chartData: [
                    {
                        name: 'Event Driven',
                        data: [...strategyEventDrivenValues],
                    },
                    {
                        name: 'Long Short Equity',
                        data: [...strategyLongShortEquityValues],
                    },
                    {
                        name: 'Global Macro',
                        data: [...strategyGlobalMacroValues],
                    },
                    {
                        name: 'Relative Value',
                        data: [...strategyRelativeValueValues],
                    },
                ],
            },
        };
    }
    generatePortfolioExposureData(portfolioDTO, portfolioGrossExposureValues, portfolioLongExposureValues, portfolioNetExposureValues, portfolioShortExposureValues) {
        var _a;
        (_a = portfolioDTO === null || portfolioDTO === void 0 ? void 0 : portfolioDTO.exposureallocation) === null || _a === void 0 ? void 0 : _a.forEach((val) => {
            if (val.strtgyallocbrkdwnname === 'Gross Exposure') {
                portfolioGrossExposureValues.push(Number.parseFloat(val.strtgyallocbrkdwnpct));
            }
            if (val.strtgyallocbrkdwnname === 'Long Exposure') {
                portfolioLongExposureValues.push(Number.parseFloat(val.strtgyallocbrkdwnpct));
            }
            if (val.strtgyallocbrkdwnname === 'Net Exposure') {
                portfolioNetExposureValues.push(Number.parseFloat(val.strtgyallocbrkdwnpct));
            }
            if (val.strtgyallocbrkdwnname === 'Short Exposure') {
                portfolioShortExposureValues.push(Number.parseFloat(val.strtgyallocbrkdwnpct));
            }
        });
    }
    generateStrategyData(portfolioDTO, strategyEventDrivenValues, strategyLongShortEquityValues, strategyGlobalMacroValues, strategyRelativeValueValues, strategyDates) {
        var _a;
        (_a = portfolioDTO === null || portfolioDTO === void 0 ? void 0 : portfolioDTO.hedgeallocationhistory) === null || _a === void 0 ? void 0 : _a.forEach((val) => {
            const findIndex = strategyDates.findIndex((date) => date === val.asofdate);
            if (findIndex === -1) {
                strategyDates.push(val.asofdate);
            }
            strategyDates = this.sortDates(strategyDates);
        });
        strategyDates === null || strategyDates === void 0 ? void 0 : strategyDates.forEach((date) => {
            var _a;
            const dataForParticularDate = (_a = portfolioDTO === null || portfolioDTO === void 0 ? void 0 : portfolioDTO.hedgeallocationhistory) === null || _a === void 0 ? void 0 : _a.filter((data) => data.asofdate === date);
            strategyEventDrivenValues.push(Number.parseFloat(dataForParticularDate.filter((data) => data.strtgyallocbrkdwnname === 'Event Driven')[0].strtgyallocbrkdwnpct));
            strategyLongShortEquityValues.push(Number.parseFloat(dataForParticularDate.filter((data) => data.strtgyallocbrkdwnname === 'Long Short Equity')[0].strtgyallocbrkdwnpct));
            strategyGlobalMacroValues.push(Number.parseFloat(dataForParticularDate.filter((data) => data.strtgyallocbrkdwnname === 'Global Macro')[0].strtgyallocbrkdwnpct));
            strategyRelativeValueValues.push(Number.parseFloat(dataForParticularDate.filter((data) => data.strtgyallocbrkdwnname === 'Relative Value')[0].strtgyallocbrkdwnpct));
        });
    }
    generateSecurityExposureData(portfolioDTO, securityExposureCategories, securityExposureGrossValues, securityExposureNetValues) {
        var _a;
        let grossValues = [];
        let netValues = [];
        (_a = portfolioDTO === null || portfolioDTO === void 0 ? void 0 : portfolioDTO.securityexposure) === null || _a === void 0 ? void 0 : _a.forEach((val) => {
            let data = val.brkdwnpct;
            if ('SECURITYGROSS' === val.exposuretypestd) {
                grossValues.push({ category: val.exposurecategory, value: Number.parseFloat(data) });
            }
            else if ('SECURITYNET' === val.exposuretypestd) {
                netValues.push({ category: val.exposurecategory, value: Number.parseFloat(data) });
            }
        });
        const sortedGrossValues = grossValues.sort((a, b) => {
            if (Number.parseFloat(a.value) > Number.parseFloat(b.value)) {
                return -1;
            }
            else if (Number.parseFloat(b.value) > Number.parseFloat(a.value)) {
                return 1;
            }
            return 0;
        });
        sortedGrossValues.forEach((val) => {
            securityExposureCategories.push(val.category);
            securityExposureGrossValues.push(val.value);
        });
        netValues = netValues.sort((a, b) => securityExposureCategories.indexOf(a.category) - securityExposureCategories.indexOf(b.category));
        netValues.forEach((val) => {
            securityExposureNetValues.push(val.value);
        });
    }
    formatHoldingsData(holdingsFullPositive, holdingsPositiveHeaders, holdingsPositiveValues, holdingsFullNegative, holdingsNegativeHeaders, holdingsNegativeValues) {
        holdingsFullPositive.forEach((val) => {
            holdingsPositiveHeaders.push(val.name);
            holdingsPositiveValues.push({ name: val.name, y: val.y });
        });
        holdingsFullNegative.forEach((val) => {
            holdingsNegativeHeaders.push(val.name);
            holdingsNegativeValues.push({ name: val.name, y: val.y });
        });
    }
    sortNegativeHoldingsASC(holdingsFullNegative) {
        holdingsFullNegative = holdingsFullNegative.sort((a, b) => {
            if (Number.parseFloat(a.y) > Number.parseFloat(b.y)) {
                return 1;
            }
            else if (Number.parseFloat(b.y) > Number.parseFloat(a.y)) {
                return -1;
            }
            return 0;
        });
        return holdingsFullNegative;
    }
    sortPositiveHoldingsDesc(holdingsFullPositive) {
        holdingsFullPositive = holdingsFullPositive.sort((a, b) => {
            if (Number.parseFloat(a.y) > Number.parseFloat(b.y)) {
                return -1;
            }
            else if (Number.parseFloat(b.y) > Number.parseFloat(a.y)) {
                return 1;
            }
            return 0;
        });
        return holdingsFullPositive;
    }
    generateInitialHoldings(portfolioDTO, holdingsFullPositive, holdingsFullNegative) {
        var _a;
        (_a = portfolioDTO === null || portfolioDTO === void 0 ? void 0 : portfolioDTO.topholdings) === null || _a === void 0 ? void 0 : _a.forEach((val) => {
            if (Number.parseFloat(val.brkdwnpct) > 0) {
                holdingsFullPositive.push({ name: val.hldngname, y: Number.parseFloat(val.brkdwnpct) });
            }
            else {
                holdingsFullNegative.push({ name: val.hldngname, y: Number.parseFloat(val.brkdwnpct) });
            }
        });
    }
    generateHedgeManagerAllocationData(portfolioDTO, hedgeManagerAllocationHeaders, hedgeManagerAllocationData, hedgeManagerChartData) {
        var _a;
        (_a = portfolioDTO === null || portfolioDTO === void 0 ? void 0 : portfolioDTO.hedgemanagerallocation) === null || _a === void 0 ? void 0 : _a.forEach((val) => {
            if ('1' === val.forceorder) {
                hedgeManagerAllocationHeaders.push({ name: val.mngrrosterstrtgy, value: val.allocation + '%' });
            }
            else {
                hedgeManagerAllocationData.push({ name: val.mngrroster, value: val.allocation, strategy: val.mngrrosterstrtgy });
            }
        });
        hedgeManagerAllocationHeaders.forEach((val) => {
            let header = val;
            let hedgeManagerData = [];
            hedgeManagerAllocationData.forEach((data) => {
                if (data.strategy === val.name) {
                    hedgeManagerData.push({ name: data.name, value: data.value + '%' });
                }
            });
            hedgeManagerChartData.push({ header: header, data: this.sortArrayDesc(hedgeManagerData) });
        });
    }
    generateGeographyData(portfolioDTO, geographyCategories, geographyGrossValues, geographyNetValues) {
        var _a;
        let grossValues = [];
        let netValues = [];
        (_a = portfolioDTO === null || portfolioDTO === void 0 ? void 0 : portfolioDTO.countryallocation) === null || _a === void 0 ? void 0 : _a.forEach((val) => {
            let data = val.brkdwnpct;
            if ('Gross Exposure' === val.calcname) {
                grossValues.push({ category: val.brkdwnname, value: Number.parseFloat(data) });
            }
            else if ('Net Exposure' === val.calcname) {
                netValues.push({ category: val.brkdwnname, value: Number.parseFloat(data) });
            }
        });
        const sortedGrossValues = grossValues.sort((a, b) => {
            if (Number.parseFloat(a.value) > Number.parseFloat(b.value)) {
                return -1;
            }
            else if (Number.parseFloat(b.value) > Number.parseFloat(a.value)) {
                return 1;
            }
            return 0;
        });
        sortedGrossValues.forEach((val) => {
            geographyCategories.push(val.category);
            geographyGrossValues.push(val.value);
        });
        netValues = netValues.sort((a, b) => geographyCategories.indexOf(a.category) - geographyCategories.indexOf(b.category));
        netValues.forEach((val) => {
            geographyNetValues.push(val.value);
        });
        if (!geographyCategories.length) {
            netValues.forEach((val) => {
                geographyCategories.push(val.category);
            });
        }
    }
    generateAssetAllocationData(portfolioDTO, assetAllocationCategories, assetAllocationGrossValues, assetAllocationNetValues) {
        var _a;
        let grossValues = [];
        let netValues = [];
        (_a = portfolioDTO === null || portfolioDTO === void 0 ? void 0 : portfolioDTO.assetallocation) === null || _a === void 0 ? void 0 : _a.forEach((val) => {
            let data = val.brkdwnpct;
            if (val.assetalloccatgstd.indexOf('K2_GROSS') > 0) {
                grossValues.push({ category: val.assetalloccatg, value: Number.parseFloat(data) });
            }
            else if (val.assetalloccatgstd.indexOf('K2_NET') > 0) {
                netValues.push({ category: val.assetalloccatg, value: Number.parseFloat(data) });
            }
        });
        const sortedGrossValues = grossValues.sort((a, b) => {
            if (Number.parseFloat(a.value) > Number.parseFloat(b.value)) {
                return -1;
            }
            else if (Number.parseFloat(b.value) > Number.parseFloat(a.value)) {
                return 1;
            }
            return 0;
        });
        sortedGrossValues.forEach((val) => {
            assetAllocationCategories.push(val.category);
            assetAllocationGrossValues.push(val.value);
        });
        netValues = netValues.sort((a, b) => assetAllocationCategories.indexOf(a.category) - assetAllocationCategories.indexOf(b.category));
        netValues.forEach((val) => {
            assetAllocationNetValues.push(val.value);
        });
    }
    sortArrayDesc(arrayToBeSorted) {
        return arrayToBeSorted.sort((a, b) => {
            if (Number.parseFloat(a.value.replace('%', '')) > Number.parseFloat(b.value.replace('%', ''))) {
                return -1;
            }
            else if (Number.parseFloat(b.value.replace('%', '')) > Number.parseFloat(a.value.replace('%', ''))) {
                return 1;
            }
            return 0;
        });
    }
    sortDates(arrayToBeSorted) {
        return arrayToBeSorted.sort((a, b) => {
            let dateA = new Date(a);
            let dateB = new Date(b);
            return dateA > dateB ? 1 : dateA < dateB ? -1 : 0;
        });
    }
}

class MapperFactory {
    createMapper(mapper, params) {
        return new mapper(params, this);
    }
}
MapperFactory.ɵfac = function MapperFactory_Factory(t) { return new (t || MapperFactory)(); };
MapperFactory.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: MapperFactory, factory: MapperFactory.ɵfac, providedIn: 'root' });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(MapperFactory, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], null, null); })();

class ProductMapper extends Mapper {
    constructor(mapperParams, mapperFactory) {
        super(mapperParams, mapperFactory);
    }
    toDomain(productDTO) {
        return {
            fundId: productDTO.fundid,
            fundName: productDTO.fundname,
            shareClasses: productDTO.shareclass
                ? this.createMapper(ShareClassesMapper).toDomain(productDTO.shareclass)
                : null,
        };
    }
}

class RiskStatsSummariesMapper extends Mapper {
    constructor(mapperParams, mapperFactory) {
        super(mapperParams, mapperFactory);
    }
    toDomain(riskStatsSummariesDTO) {
        const riskStatsSummaries = [];
        riskStatsSummariesDTO.forEach((riskStatsSummaryDTO) => {
            const riskStatsSummary = this.createMapper(RiskStatsSummaryMapper).toDomain(riskStatsSummaryDTO);
            if (riskStatsSummary) {
                riskStatsSummaries.push(riskStatsSummary);
            }
        });
        return riskStatsSummaries;
    }
}
class RiskStatsSummaryMapper extends Mapper {
    constructor(mapperParams, mapperFactory) {
        super(mapperParams, mapperFactory);
    }
    toDomain(riskStatsSummaryDTO) {
        return {
            asOfDate: riskStatsSummaryDTO.asofdate,
            calcName: riskStatsSummaryDTO.calcname,
            benchmarkName: riskStatsSummaryDTO.bmname,
            displayOrder: riskStatsSummaryDTO.disporder,
            elementNameStd: riskStatsSummaryDTO.elemnamestd,
            elementName: riskStatsSummaryDTO.elemname,
            element3YearValue: riskStatsSummaryDTO.elemval3yr,
            element5YearValue: riskStatsSummaryDTO.elemval5yr,
            element10YearValue: riskStatsSummaryDTO.elemval10yr,
        };
    }
}

class ChargesMapper extends Mapper {
    constructor(mapperParams, mapperFactory) {
        super(mapperParams, mapperFactory);
    }
    toDomain(chargesDTO) {
        return {
            expenseRatioGross: chargesDTO.expratgross,
            expenseRatioNet: chargesDTO.expratnet,
            maximumInitialCharge: chargesDTO.maxinitchrg,
            cdsc: chargesDTO.cdsc,
            managementFee: chargesDTO.managementfee,
            performanceFee: chargesDTO.prfrmncefee,
        };
    }
}

class ShareClassesMapper extends Mapper {
    constructor(mapperParams, mapperFactory) {
        super(mapperParams, mapperFactory);
    }
    toDomain(shareClassesDTO) {
        if ((shareClassesDTO === null || shareClassesDTO === void 0 ? void 0 : shareClassesDTO.length) >= 0) {
            const shareClasses = [];
            shareClassesDTO.forEach((shareClassDTO) => {
                const shareClass = this.createMapper(ShareClassMapper).toDomain(shareClassDTO);
                if (shareClass) {
                    shareClasses.push(shareClass);
                }
            });
            return shareClasses;
        }
        return null;
    }
}
class ShareClassMapper extends Mapper {
    constructor(mapperParams, mapperFactory) {
        super(mapperParams, mapperFactory);
    }
    toDomain(shareClassDTO) {
        var _a, _b;
        const fundId = (_a = shareClassDTO.identifiers) === null || _a === void 0 ? void 0 : _a.fundid;
        const shareClassCode = (_b = shareClassDTO.identifiers) === null || _b === void 0 ? void 0 : _b.shclcode;
        const fundShareClassId = (shareClassCode ? `${fundId}-${shareClassCode}` : fundId);
        return {
            shareClassCode: shareClassDTO.shclcode,
            shareClassName: shareClassDTO.shclname,
            shareClassCurrency: shareClassDTO.shclcurr,
            fundShareClassId: fundShareClassId ? fundShareClassId : null,
            isPrimaryShareClass: shareClassDTO.prmryshclind
                ? shareClassDTO.prmryshclind.toLowerCase() === 'yes'
                : null,
            performanceInceptionDate: shareClassDTO.perfincdt,
            charges: shareClassDTO.charges
                ? this.createMapper(ChargesMapper).toDomain(shareClassDTO.charges)
                : null,
            identifiers: shareClassDTO.identifiers
                ? this.createMapper(IdentifiersMapper).toDomain(shareClassDTO.identifiers)
                : null,
            nav: shareClassDTO.nav
                ? this.createMapper(NavMapper).toDomain(shareClassDTO.nav)
                : null,
            performance: shareClassDTO.performance
                ? this.createMapper(PerformanceMapper).toDomain(shareClassDTO.performance)
                : null,
            benchmarkPerformance: shareClassDTO.bmperformance
                ? this.createMapper(BMPerformanceMapper).toDomain(shareClassDTO.bmperformance)
                : null,
            distribution: shareClassDTO.distribution
                ? this.createMapper(DistributionsMapper).toDomain(shareClassDTO.distribution)
                : null,
            riskStatsSummary: shareClassDTO.riskstatssummary
                ? this.createMapper(RiskStatsSummariesMapper).toDomain(shareClassDTO.riskstatssummary)
                : null,
            salesChargeBreakpoints: shareClassDTO.saleschargebreakpoints
                ? this.createMapper(SalesChargeBreakpointsMapper).toDomain(shareClassDTO.saleschargebreakpoints)
                : null,
        };
    }
}

class IdentifiersMapper extends Mapper {
    constructor(mapperParams, mapperFactory) {
        super(mapperParams, mapperFactory);
    }
    toDomain(identifiersDto) {
        return {
            fundId: identifiersDto.fundid,
            shareClassCode: identifiersDto.shclcode,
            ticker: identifiersDto.ticker,
        };
    }
}

class NavMapper extends Mapper {
    constructor(mapperParams, mapperFactory) {
        super(mapperParams, mapperFactory);
    }
    toDomain(navDto) {
        return {
            navDate: navDto.navdate,
            navValue: navDto.navvalue,
            navChangeValue: navDto.navchngvalue,
            navChangePercentage: navDto.navchngpct,
        };
    }
}

class PerformanceMapper extends Mapper {
    constructor(mapperParams, mapperFactory) {
        super(mapperParams, mapperFactory);
    }
    toDomain(performanceDTO) {
        var _a, _b;
        return {
            monthEnd: ((_a = performanceDTO.month_end) === null || _a === void 0 ? void 0 : _a.length) >= 0
                ? performanceDTO.month_end.map((perf) => this.createMapper(PerformanceCurrentMapper).toDomain(perf))
                : null,
            quarterEnd: ((_b = performanceDTO.quarter_end) === null || _b === void 0 ? void 0 : _b.length) >= 0
                ? performanceDTO.quarter_end.map((perf) => this.createMapper(PerformanceCurrentMapper).toDomain(perf))
                : null,
        };
    }
}
class PerformanceCurrentMapper extends Mapper {
    constructor(mapperParams, mapperFactory) {
        super(mapperParams, mapperFactory);
    }
    toDomain(performanceCurrentDTO) {
        return {
            performanceAsOfDate: performanceCurrentDTO.asofdate,
            performanceAsOfDateStd: performanceCurrentDTO.asofdatestd,
            calcNameStd: performanceCurrentDTO.calcnamestd,
            averageAnnualYearToDateReturn: performanceCurrentDTO.avgannretytd,
            averageAnnual1YearReturn: performanceCurrentDTO.avgannret1yr,
            averageAnnual3YearReturn: performanceCurrentDTO.avgannret3yr,
            averageAnnual5YearReturn: performanceCurrentDTO.avgannret5yr,
            averageAnnual10YearReturn: performanceCurrentDTO.avgannret10yr,
            averageAnnualSinceInceptionReturn: performanceCurrentDTO.avgannretactinc,
            cumulative1MonthReturn: performanceCurrentDTO.cummret1mth,
            cumulative3MonthReturn: performanceCurrentDTO.cummret3mth,
            cumulative6MonthReturn: performanceCurrentDTO.cummret6mth,
            cumulativeYearToDateReturn: performanceCurrentDTO.cummretytd,
            cumulative1YearReturn: performanceCurrentDTO.cummret1yr,
            cumulative3YearReturn: performanceCurrentDTO.cummret3yr,
            cumulative5YearReturn: performanceCurrentDTO.cummret5yr,
            cumulative10YearReturn: performanceCurrentDTO.cummret10yr,
            cumulative15YearReturn: performanceCurrentDTO.cummret15yr,
            cumulativeSinceInceptionReturn: performanceCurrentDTO.cummretactinc,
            calendarYear1YearReturn: performanceCurrentDTO.calendaryrret1yr,
            calendarYear2YearReturn: performanceCurrentDTO.calendaryrret2yr,
            calendarYear3YearReturn: performanceCurrentDTO.calendaryrret3yr,
            calendarYear4YearReturn: performanceCurrentDTO.calendaryrret4yr,
            calendarYear5YearReturn: performanceCurrentDTO.calendaryrret5yr,
            calendarYear6YearReturn: performanceCurrentDTO.calendaryrret6yr,
            calendarYear7YearReturn: performanceCurrentDTO.calendaryrret7yr,
            calendarYear8YearReturn: performanceCurrentDTO.calendaryrret8yr,
            calendarYear9YearReturn: performanceCurrentDTO.calendaryrret9yr,
            calendarYear10YearReturn: performanceCurrentDTO.calendaryrret10yr,
        };
    }
}

class BMPerformanceMapper extends Mapper {
    constructor(mapperParams, mapperFactory) {
        super(mapperParams, mapperFactory);
    }
    toDomain(bmPerformanceDTO) {
        var _a, _b;
        return {
            monthEnd: ((_a = bmPerformanceDTO.month_end) === null || _a === void 0 ? void 0 : _a.length) >= 0
                ? bmPerformanceDTO.month_end.map((perf) => this.createMapper(BMPerformanceCurrentMapper).toDomain(perf))
                : null,
            quarterEnd: ((_b = bmPerformanceDTO.quarter_end) === null || _b === void 0 ? void 0 : _b.length) >= 0
                ? bmPerformanceDTO.quarter_end.map((perf) => this.createMapper(BMPerformanceCurrentMapper).toDomain(perf))
                : null,
        };
    }
}
class BMPerformanceCurrentMapper extends Mapper {
    constructor(mapperParams, mapperFactory) {
        super(mapperParams, mapperFactory);
    }
    toDomain(bmPerformanceCurrentDTO) {
        return {
            performanceAsOfDate: bmPerformanceCurrentDTO.asofdate,
            performanceAsOfDateStd: bmPerformanceCurrentDTO.asofdatestd,
            calcNameStd: bmPerformanceCurrentDTO.calcnamestd,
            benchmarkName: bmPerformanceCurrentDTO.bmname,
            benchmarkLabel: bmPerformanceCurrentDTO.bmlabel,
            benchmarkAverageAnnualYearToDateReturn: bmPerformanceCurrentDTO.bmavgannretytd,
            benchmarkAverageAnnual1YearReturn: bmPerformanceCurrentDTO.bmavgannret1yr,
            benchmarkAverageAnnual3YearReturn: bmPerformanceCurrentDTO.bmavgannret3yr,
            benchmarkAverageAnnual5YearReturn: bmPerformanceCurrentDTO.bmavgannret5yr,
            benchmarkAverageAnnual10YearReturn: bmPerformanceCurrentDTO.bmavgannret10yr,
            benchmarkAverageAnnualSinceInceptionReturn: bmPerformanceCurrentDTO.bmavgannretactinc,
            benchmarkCumulative1MonthReturn: bmPerformanceCurrentDTO.bmcummret1mth,
            benchmarkCumulative3MonthReturn: bmPerformanceCurrentDTO.bmcummret3mth,
            benchmarkCumulative6MonthReturn: bmPerformanceCurrentDTO.bmcummret6mth,
            benchmarkCumulativeYearToDateReturn: bmPerformanceCurrentDTO.bmcummretytd,
            benchmarkCumulative1YearReturn: bmPerformanceCurrentDTO.bmcummret1yr,
            benchmarkCumulative3YearReturn: bmPerformanceCurrentDTO.bmcummret3yr,
            benchmarkCumulative5YearReturn: bmPerformanceCurrentDTO.bmcummret5yr,
            benchmarkCumulative10YearReturn: bmPerformanceCurrentDTO.bmcummret10yr,
            benchmarkCumulative15YearReturn: bmPerformanceCurrentDTO.bmcummret15yr,
            benchmarkCumulativeSinceInceptionReturn: bmPerformanceCurrentDTO.bmcummretactinc,
            benchmarkCalendarYear1YearReturn: bmPerformanceCurrentDTO.bmcalendaryrret1yr,
            benchmarkCalendarYear2YearReturn: bmPerformanceCurrentDTO.bmcalendaryrret2yr,
            benchmarkCalendarYear3YearReturn: bmPerformanceCurrentDTO.bmcalendaryrret3yr,
            benchmarkCalendarYear4YearReturn: bmPerformanceCurrentDTO.bmcalendaryrret4yr,
            benchmarkCalendarYear5YearReturn: bmPerformanceCurrentDTO.bmcalendaryrret5yr,
            benchmarkCalendarYear6YearReturn: bmPerformanceCurrentDTO.bmcalendaryrret6yr,
            benchmarkCalendarYear7YearReturn: bmPerformanceCurrentDTO.bmcalendaryrret7yr,
            benchmarkCalendarYear8YearReturn: bmPerformanceCurrentDTO.bmcalendaryrret8yr,
            benchmarkCalendarYear9YearReturn: bmPerformanceCurrentDTO.bmcalendaryrret9yr,
            benchmarkCalendarYear10YearReturn: bmPerformanceCurrentDTO.bmcalendaryrret10yr,
        };
    }
}

class DistributionsMapper extends Mapper {
    constructor(mapperParams, mapperFactory) {
        super(mapperParams, mapperFactory);
    }
    toDomain(distributionsDTO) {
        if ((distributionsDTO === null || distributionsDTO === void 0 ? void 0 : distributionsDTO.length) >= 0) {
            const distributions = [];
            distributionsDTO.forEach((distributionDTO) => {
                const distribution = this.createMapper(DistributionMapper).toDomain(distributionDTO);
                if (distribution) {
                    distributions.push(distribution);
                }
            });
            return distributions;
        }
        return null;
    }
}
class DistributionMapper extends Mapper {
    constructor(mapperParams, mapperFactory) {
        super(mapperParams, mapperFactory);
    }
    toDomain(distributionDTO) {
        return {
            distributionAsOfDate: distributionDTO.asofdate,
            distributionAsOfDateStd: distributionDTO.asofdatestd,
        };
    }
}

class DistributionHistoriesMapper extends Mapper {
    constructor(mapperParams, mapperFactory) {
        super(mapperParams, mapperFactory);
    }
    toDomain(distributionHistoriesDto) {
        const distributionHistories = [];
        distributionHistoriesDto.forEach((distributionHistoryDto) => {
            const distributionHistory = this.createMapper(DistributionHistoryMapper).toDomain(distributionHistoryDto);
            if (distributionHistory) {
                distributionHistories.push(distributionHistory);
            }
        });
        return distributionHistories;
    }
}
class DistributionHistoryMapper extends Mapper {
    constructor(mapperParams, mapperFactory) {
        super(mapperParams, mapperFactory);
    }
    toDomain(distributionHistoryDto) {
        return {
            asOfDate: distributionHistoryDto.asofdate,
            asOfDateStd: distributionHistoryDto.asofdatestd,
            incomeDistributionRecordDate: distributionHistoryDto.incmdistrecorddate,
            incomeDistributionExDividendDate: distributionHistoryDto.incmdistexdivdate,
            reinvestmentDate: distributionHistoryDto.reinvstmntdate,
            incomeDistributionExPayableDate: distributionHistoryDto.incmdistexpayabledate,
            reinvestmentAmount: distributionHistoryDto.reinvstmntamt,
            longTermCapitalGainAmount: distributionHistoryDto.capgainamtlongterm,
            shortTermCapitalGainAmount: distributionHistoryDto.capgainamtshortterm,
            incomeDistributionAmount: distributionHistoryDto.incmdistamt,
            returnOfCapitalAmount: distributionHistoryDto.returnofcapitalamt,
            totalDistributionAmountMonthly: distributionHistoryDto.totaldistamtmth,
        };
    }
}

class ChartsMapper extends Mapper {
    constructor(mapperParams, mapperFactory) {
        super(mapperParams, mapperFactory);
        dayjs.extend(quarterOfYear);
    }
    toDomain(chartsDTO) {
        return {
            cumulativePerformanceHistory: chartsDTO.cummperfhist
                ? this.createMapper(ChartTypeMapper).toDomain(chartsDTO.cummperfhist)
                : null,
        };
    }
}
class ChartTypeMapper extends Mapper {
    constructor(mapperParams, mapperFactory) {
        super(mapperParams, mapperFactory);
    }
    toDomain(chartTypeDTO) {
        return {
            _10k: chartTypeDTO._10k
                ? this.createMapper(CumulativePerformanceHistoryTimePeriodMapper).toDomain(chartTypeDTO._10k)
                : null,
        };
    }
}
class CumulativePerformanceHistoryTimePeriodMapper extends Mapper {
    constructor(mapperParams, mapperFactory) {
        super(mapperParams, mapperFactory);
    }
    toDomain(cummPerfHistTimePeriodDTO) {
        return {
            sinceInception: cummPerfHistTimePeriodDTO.sinceinception
                ? this.createMapper(CumulativePerformanceHistoryCategoryMapper).toDomain(cummPerfHistTimePeriodDTO.sinceinception)
                : null,
        };
    }
}
class CumulativePerformanceHistoryCategoryMapper extends Mapper {
    constructor(mapperParams, mapperFactory) {
        super(mapperParams, mapperFactory);
    }
    toDomain(cummPerfHistCategoryDTO) {
        var _a;
        const fundCumulativePerformanceHistory = (_a = cummPerfHistCategoryDTO.fund) === null || _a === void 0 ? void 0 : _a.map((perf) => perf
            ? this.createMapper(FundCumulativePerformanceHistoryMapper).toDomain(perf)
            : null);
        const benchmarkCumulativePerformanceHistoryCategory = cummPerfHistCategoryDTO.benchmark
            ? this.createMapper(BenchmarkCumulativePerformanceHistoryCategoryMapper).toDomain(cummPerfHistCategoryDTO.benchmark)
            : null;
        return {
            fund: fundCumulativePerformanceHistory,
            benchmark: benchmarkCumulativePerformanceHistoryCategory,
            fundQuarterly: getCumulativeQuarterlyRecords(fundCumulativePerformanceHistory),
            benchmarkQuarterly: benchmarkCumulativePerformanceHistoryCategory
                ? {
                    primary: getCumulativeQuarterlyRecords(benchmarkCumulativePerformanceHistoryCategory.primary),
                    secondary: getCumulativeQuarterlyRecords(benchmarkCumulativePerformanceHistoryCategory.secondary),
                }
                : null,
        };
    }
}
class FundCumulativePerformanceHistoryMapper extends Mapper {
    constructor(mapperParams, mapperFactory) {
        super(mapperParams, mapperFactory);
    }
    toDomain(fundCummPerfHistDTO) {
        return {
            asOfDate: fundCummPerfHistDTO.asofdate,
            asOfDateStd: fundCummPerfHistDTO.asofdatestd,
            calcName: fundCummPerfHistDTO.calcname,
            cumulativeMonthlyReturnActualStd: fundCummPerfHistDTO.cummmonthlyretactstd,
            cumulativeMonthlyReturnCalculated: fundCummPerfHistDTO.cummmonthlyretcalc,
            cumulativeMonthlyReturnCalculatedStd: fundCummPerfHistDTO.cummmonthlyretcalcstd,
        };
    }
}
class BenchmarkCumulativePerformanceHistoryCategoryMapper extends Mapper {
    constructor(mapperParams, mapperFactory) {
        super(mapperParams, mapperFactory);
    }
    toDomain(bmCummPerfHistCategoryDTO) {
        var _a, _b;
        return {
            primary: (_a = bmCummPerfHistCategoryDTO === null || bmCummPerfHistCategoryDTO === void 0 ? void 0 : bmCummPerfHistCategoryDTO.primary) === null || _a === void 0 ? void 0 : _a.map((perf) => perf
                ? this.createMapper(BenchmarkCumulativePerformanceHistoryMapper).toDomain(perf)
                : null),
            secondary: (_b = bmCummPerfHistCategoryDTO === null || bmCummPerfHistCategoryDTO === void 0 ? void 0 : bmCummPerfHistCategoryDTO.secondary) === null || _b === void 0 ? void 0 : _b.map((perf) => perf
                ? this.createMapper(BenchmarkCumulativePerformanceHistoryMapper).toDomain(perf)
                : null),
        };
    }
}
class BenchmarkCumulativePerformanceHistoryMapper extends Mapper {
    constructor(mapperParams, mapperFactory) {
        super(mapperParams, mapperFactory);
    }
    toDomain(bmCummPerfHistDTO) {
        return {
            asOfDate: bmCummPerfHistDTO.asofdate,
            asOfDateStd: bmCummPerfHistDTO.asofdatestd,
            calcName: bmCummPerfHistDTO.calcname,
            cumulativeMonthlyReturnActualStd: bmCummPerfHistDTO.cummmonthlyretactstd,
            cumulativeMonthlyReturnCalculated: bmCummPerfHistDTO.cummmonthlyretcalc,
            cumulativeMonthlyReturnCalculatedStd: bmCummPerfHistDTO.cummmonthlyretcalcstd,
        };
    }
}
/**
 * Filter performance history records upto the last quarter.
 * This works for both fund and benchmark cumulative performance history monthly records.
 * @param performanceHistory fund or benchmark cumulative performance history monthly records
 */
function getCumulativeQuarterlyRecords(performanceHistory) {
    const currentQuarterStartDateStd = dayjs()
        .subtract(1, 'quarter')
        .endOf('quarter')
        .add(1, 'day')
        .format('YYYY-MM-DD');
    return performanceHistory.filter((perf) => dayjs(perf.asOfDateStd).isBefore(currentQuarterStartDateStd));
}

class PortfolioMapper extends Mapper {
    constructor(mapperParams, mapperFactory) {
        super(mapperParams, mapperFactory);
    }
    toDomain(portfolioDTO) {
        var _a;
        return {
            portfolioChars: (_a = portfolioDTO.portfolio) === null || _a === void 0 ? void 0 : _a.portfoliochars,
        };
    }
}

class SalesChargeBreakpointsMapper extends Mapper {
    constructor(mapperParam, mapperFactory) {
        super(mapperParam, mapperFactory);
    }
    toDomain(salesChargeBreakpointsDTO) {
        if ((salesChargeBreakpointsDTO === null || salesChargeBreakpointsDTO === void 0 ? void 0 : salesChargeBreakpointsDTO.length) > 0) {
            const salesChargeBreakpoints = [];
            salesChargeBreakpointsDTO.forEach((salesChargeBreakpointDTO) => {
                const salesChargeBreakpoint = this.createMapper(SalesChargeBreakpointMapper).toDomain(salesChargeBreakpointDTO);
                if (salesChargeBreakpoint) {
                    salesChargeBreakpoints.push(salesChargeBreakpoint);
                }
            });
            return salesChargeBreakpoints;
        }
        return null;
    }
}
class SalesChargeBreakpointMapper extends Mapper {
    constructor(mapperParam, mapperFactory) {
        super(mapperParam, mapperFactory);
    }
    toDomain(salesChargeBreakpointDTO) {
        return {
            breakPointTypeRange: salesChargeBreakpointDTO.brkpnttyprng,
            distributionRateBreakPoint: salesChargeBreakpointDTO.distribratebrkpnt,
            breakPointTypeKey: salesChargeBreakpointDTO.brkpnttypkey
        };
    }
}

class PortfolioDetailsService extends GraphQLDataService {
    constructor(apollo, 
    // siteConfigService: SiteConfigService,
    mapperFactory) {
        super(apollo);
        this.mapperFactory = mapperFactory;
    }
    /**
     * Fetch the product response and map the raw data to the expected model.
     * @param query query to be hit on graphql url
     * @param fundDataServiceParams query variables to be passed
     */
    register(query, fundDataServiceParams) {
        return super.register(query, this.getVariables(fundDataServiceParams)).pipe(map((portfolioDetail) => {
            var _a;
            const portfolioDTO = ((_a = portfolioDetail === null || portfolioDetail === void 0 ? void 0 : portfolioDetail.data) === null || _a === void 0 ? void 0 : _a.Portfolio)
                ? portfolioDetail.data.Portfolio.portfolio
                : null;
            switch (fundDataServiceParams.fundId) {
                case '92083': {
                    return this.mapperFactory.createMapper(PortfolioMapperCPREX, null).toDomain(portfolioDTO);
                }
                case '29429': {
                    return this.mapperFactory.createMapper(PortfolioMapperBSP, null).toDomain(portfolioDTO);
                }
                case '18120': {
                    return this.mapperFactory.createMapper(PortfolioMapperK2, null).toDomain(portfolioDTO);
                }
                case '90022': {
                    return this.mapperFactory.createMapper(PortfolioMapperBSP, null).toDomain(portfolioDTO);
                }
                default: {
                    return null;
                }
            }
        }));
    }
    /**
     * Return required variables value to graphql query.
     * @param fundDataServiceParams params required for graphql query
     */
    getVariables(dataServiceParams) {
        return {
            countrycode: this.getCountry(),
            languagecode: this.getLanguage(),
            fundid: dataServiceParams.fundId,
        };
    }
}
PortfolioDetailsService.ɵfac = function PortfolioDetailsService_Factory(t) { return new (t || PortfolioDetailsService)(i0.ɵɵinject(i1.Apollo), i0.ɵɵinject(MapperFactory)); };
PortfolioDetailsService.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: PortfolioDetailsService, factory: PortfolioDetailsService.ɵfac, providedIn: 'root' });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PortfolioDetailsService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: i1.Apollo }, { type: MapperFactory }]; }, null); })();

class ProductDetailsService extends GraphQLDataService {
    constructor(apollo, 
    // siteConfigService: SiteConfigService,
    mapperFactory) {
        super(apollo);
        this.mapperFactory = mapperFactory;
    }
    /**
     * Fetch the product detailed response and map the raw data to the expected model.
     * @param query query to be hit on graphql url
     * @param fundDataServiceParams query variables to be passed
     */
    register(query, fundDataServiceParams) {
        return super.register(query, this.getVariables(fundDataServiceParams)).pipe(map((productDetail) => {
            var _a;
            const productDto = ((_a = productDetail === null || productDetail === void 0 ? void 0 : productDetail.data) === null || _a === void 0 ? void 0 : _a.ProductDetails)
                ? productDetail.data.ProductDetails
                : null;
            const mapperParams = {
            // config: this.siteConfigService,
            };
            return productDto
                ? this.mapperFactory
                    .createMapper(ProductMapper, mapperParams)
                    .toDomain(productDto)
                : null;
        }));
    }
    /**
     * Return required variables value to graphql query.
     * @param fundDataServiceParams params required for graphql query
     */
    getVariables(dataServiceParams) {
        return {
            countrycode: this.getCountry(),
            languagecode: this.getLanguage(),
            fundid: dataServiceParams.fundId,
        };
    }
}
ProductDetailsService.ɵfac = function ProductDetailsService_Factory(t) { return new (t || ProductDetailsService)(i0.ɵɵinject(i1.Apollo), i0.ɵɵinject(MapperFactory)); };
ProductDetailsService.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: ProductDetailsService, factory: ProductDetailsService.ɵfac, providedIn: 'root' });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ProductDetailsService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: i1.Apollo }, { type: MapperFactory }]; }, null); })();

class ProductOverviewService extends GraphQLDataService {
    constructor(apollo, 
    // siteConfigService: SiteConfigService,
    mapperFactory) {
        super(apollo);
        this.mapperFactory = mapperFactory;
    }
    /**
     * Fetch the product response and map the raw data to the expected model.
     * @param query query to be hit on graphql url
     * @param fundDataServiceParams query variables to be passed
     */
    register(query, fundDataServiceParams) {
        return super.register(query, this.getVariables(fundDataServiceParams)).pipe(map((productDetail) => {
            var _a;
            const productDto = ((_a = productDetail === null || productDetail === void 0 ? void 0 : productDetail.data) === null || _a === void 0 ? void 0 : _a.Overview)
                ? productDetail.data.Overview
                : null;
            const mapperParams = {
            // config: this.siteConfigService,
            };
            return productDto
                ? this.mapperFactory
                    .createMapper(ProductMapper, mapperParams)
                    .toDomain(productDto)
                : null;
        }));
    }
    /**
     * Return required variables value to graphql query.
     * @param fundDataServiceParams params required for graphql query
     */
    getVariables(dataServiceParams) {
        return {
            countrycode: this.getCountry(),
            languagecode: this.getLanguage(),
            fundid: dataServiceParams.fundId,
            shareclasscode: dataServiceParams.shareClassCode,
        };
    }
}
ProductOverviewService.ɵfac = function ProductOverviewService_Factory(t) { return new (t || ProductOverviewService)(i0.ɵɵinject(i1.Apollo), i0.ɵɵinject(MapperFactory)); };
ProductOverviewService.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: ProductOverviewService, factory: ProductOverviewService.ɵfac, providedIn: 'root' });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ProductOverviewService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: i1.Apollo }, { type: MapperFactory }]; }, null); })();

const ProductOverviewQuery = gql `
  query ProductOverview($fundid: String!, $shareclasscode: String!, $countrycode: String!, $languagecode: String!) {
    Overview(fundid: $fundid, shareclasscode: $shareclasscode, countrycode: $countrycode, languagecode: $languagecode) {
      fundid
      fundname
      shareclass {
        shclcode
        shclname
        perfincdt
        identifiers {
          ticker
        }
        distribution {
          asofdate
        }
        nav {
          navdate
          navvalue
          navchngvalue
          navchngpct
        }
        performance {
          month_end {
            asofdate
            asofdatestd
            calcnamestd
            avgannretytd
            avgannret1yr
            avgannret3yr
            avgannret5yr
            avgannret10yr
            avgannretactinc
            cummret1mth
            cummret3mth
            cummret6mth
            cummretytd
            cummret1yr
            cummret3yr
            cummret5yr
            cummret10yr
            cummretactinc
            calendaryrret1yr
            calendaryrret2yr
            calendaryrret3yr
            calendaryrret4yr
            calendaryrret5yr
            calendaryrret6yr
            calendaryrret7yr
            calendaryrret8yr
            calendaryrret9yr
            calendaryrret10yr
          }
          quarter_end {
            asofdate
            asofdatestd
            calcnamestd
            avgannretytd
            avgannret1yr
            avgannret3yr
            avgannret5yr
            avgannret10yr
            avgannretactinc
            cummret1mth
            cummret3mth
            cummret6mth
            cummretytd
            cummret1yr
            cummret3yr
            cummret5yr
            cummret10yr
            cummretactinc
            calendaryrret1yr
            calendaryrret2yr
            calendaryrret3yr
            calendaryrret4yr
            calendaryrret5yr
            calendaryrret6yr
            calendaryrret7yr
            calendaryrret8yr
            calendaryrret9yr
            calendaryrret10yr
          }
        }
        bmperformance {
          month_end {
            asofdate
            calcnamestd
            bmname
            bmlabel
            bmavgannret1yr
            bmavgannret3yr
            bmavgannret5yr
            bmavgannret10yr
            bmavgannretactinc
            bmcummret1mth
            bmcummret3mth
            bmcummret6mth
            bmcummretytd
            bmcummret1yr
            bmcummret3yr
            bmcummret5yr
            bmcummret10yr
            bmcummretactinc
            bmcalendaryrret1yr
            bmcalendaryrret2yr
            bmcalendaryrret3yr
            bmcalendaryrret4yr
            bmcalendaryrret5yr
            bmcalendaryrret6yr
            bmcalendaryrret7yr
            bmcalendaryrret8yr
            bmcalendaryrret9yr
            bmcalendaryrret10yr
          }
          quarter_end {
            asofdate
            calcnamestd
            bmname
            bmlabel
            bmavgannret1yr
            bmavgannret3yr
            bmavgannret5yr
            bmavgannret10yr
            bmavgannretactinc
            bmcummret1mth
            bmcummret3mth
            bmcummret6mth
            bmcummretytd
            bmcummret1yr
            bmcummret3yr
            bmcummret5yr
            bmcummret10yr
            bmcummretactinc
            bmcalendaryrret1yr
            bmcalendaryrret2yr
            bmcalendaryrret3yr
            bmcalendaryrret4yr
            bmcalendaryrret5yr
            bmcalendaryrret6yr
            bmcalendaryrret7yr
            bmcalendaryrret8yr
            bmcalendaryrret9yr
            bmcalendaryrret10yr
          }
        }
        riskstatssummary {
          asofdate
          calcname
          bmname
          disporder
          elemnamestd
          elemname
          elemval3yr
          elemval5yr
          elemval10yr
        }
        saleschargebreakpoints {
          brkpnttypkey
          brkpnttyprng
          distribratebrkpnt
        }
      }
    }
  }
`;
const ProductDetailsQuery = gql `
  query ProductDetails($fundid: String!, $countrycode: String!, $languagecode: String!) {
    ProductDetails(fundid: $fundid, countrycode: $countrycode, languagecode: $languagecode) {
      fundid
      shareclass {
        shclcode
        shclname
        charges {
          expratgross
          expratnet
          maxinitchrg
          cdsc
          managementfee
          prfrmncefee
        }
      }
    }
  }
`;
const DistributionHistoryQuery = gql `
  query DistributionHistory($fundid: String!, $shareclasscode: String!, $countrycode: String!, $languagecode: String!) {
    DistributionHistory(
      fundid: $fundid
      shareclasscode: $shareclasscode
      countrycode: $countrycode
      languagecode: $languagecode
    ) {
      distribution {
        asofdate
        asofdatestd
        incmdistrecorddate
        incmdistexdivdate
        reinvstmntdate
        incmdistexpayabledate
        reinvstmntamt
        capgainamtlongterm
        capgainamtshortterm
        incmdistamt
        returnofcapitalamt
        totaldistamtmth
      }
    }
  }
`;
const ChartsQuery = gql `
  query TimeSeries($fundid: String!, $shareclasscode: String!, $countrycode: String!, $languagecode: String!) {
    TimeSeries(fundid: $fundid, shareclasscode: $shareclasscode, countrycode: $countrycode, languagecode: $languagecode) {
      charts {
        cummperfhist {
          _10k {
            sinceinception {
              fund {
                asofdate
                asofdatestd
                calcname
                cummmonthlyretactstd
                cummmonthlyretcalcstd
              }
              benchmark {
                primary {
                  asofdate
                  asofdatestd
                  calcname
                  cummmonthlyretactstd
                  cummmonthlyretcalc
                  cummmonthlyretcalcstd
                }
                secondary {
                  asofdate
                  asofdatestd
                  calcname
                  cummmonthlyretactstd
                  cummmonthlyretcalc
                  cummmonthlyretcalcstd
                }
              }
            }
          }
        }
      }
    }
  }
`;
const PortfolioQuery = gql `
  query Portfolio($fundid: String!, $countrycode: String!, $languagecode: String!) {
    Portfolio(fundid: $fundid, countrycode: $countrycode, languagecode: $languagecode) {
      fundnamestd
      portfolio {
        portfoliochars {
          elemvaluelocal
          elemname
          elemdispname
        }
      }
    }
  }
`;
const PortfolioCPREX = gql `
  query PortfolioAssetCountrySector($fundid: String!, $countrycode: String!, $languagecode: String!) {
    Portfolio(fundid: $fundid, countrycode: $countrycode, languagecode: $languagecode) {
      portfolio {
        assetallocation {
          allocheadinglocal
          asofdate
          assetalloccatg
          brkdwnpct
          brkdwnpctstd
        }
        countryallocation {
          allocheadinglocal
          asofdate
          brkdwnname
          brkdwnpct
          brkdwnpctstd
          calcname
        }
        sectorallocation {
          allocheadinglocal
          asofdate
          brkdwnpct
          brkdwnpctstd
          sectorname
        }
      }
    }
  }
`;
const PortfolioBSP = gql `
  query PortfolioAssetCountrySector($fundid: String!, $countrycode: String!, $languagecode: String!) {
    Portfolio(fundid: $fundid, countrycode: $countrycode, languagecode: $languagecode) {
      portfolio {
        assetallocation {
          allocheadinglocal
          asofdate
          assetalloccatg
          brkdwnpct
          brkdwnpctstd
        }
        sectorallocation {
          allocheadinglocal
          asofdate
          brkdwnpct
          brkdwnpctstd
          sectorname
        }
      }
    }
  }
`;
const PortfolioK2 = gql `
  query PortfolioK2($fundid: String!, $countrycode: String!, $languagecode: String!) {
    Portfolio(fundid: $fundid, countrycode: $countrycode, languagecode: $languagecode) {
      portfolio {
        assetallocation {
          allocflag
          allocheadinglocal
          asofdate
          assetalloccatg
          assetalloccatgstd
          brkdwnpct
          brkdwnpctstd
          calcbasislocal
          calctypelocal
          frequency
          hasderivatives
          rank
        }
        countryallocation {
          allocflag
          allocheadinglocal
          asofdate
          bmbrkdwnpct
          bmbrkdwnpctstd
          brkdwnname
          brkdwnpct
          brkdwnpctstd
          calcbasislocal
          calcname
          calcnamestd
          frequency
          hascountry
          hasderivatives
          parent
          rank
          bmname
        }
        hedgeallocation {
          asofdate
          elemtypestd
          frequency
          rank
          strtgyallocbrkdwnname
          strtgyallocbrkdwnpct
          tgtallocbrkdwnrange
        }
        securityexposure {
          asofdate
          brkdwnpct
          brkdwnpctstd
          exposuretype
          exposuretypestd
          exposurecategory
          exposurecategorystd
          frequency
          rank
        }
        topholdings {
          asofdate
          hldngname
          geocode
          sectorname
          brkdwnpct
          frequency
          calcbasislocal
          calctypelocal
          allocflag
          hasderivatives
        }
        hedgemanagerallocation {
          allocation
          allocationstd
          asofdate
          forceorder
          frequency
          mngrroster
          mngrrosterstrtgy
        }
        hedgeallocationhistory {
          fundid
          cntrycode
          langcode
          asofdate
          asofdatestd
          elemtype
          elemtypestd
          strtgyallocbrkdwnname
          strtgyallocbrkdwnnamestd
          strtgyallocbrkdwnpct
          tgtallocbrkdwnmin
          tgtallocbrkdwnmax
          tgtallocbrkdwnrange
          contribytd
          contrib1mo
          contrib3mo
          contrib1yr
          rank
          frequency
        }
        exposureallocation {
          fundid
          cntrycode
          langcode
          asofdate
          asofdatestd
          elemtype
          elemtypestd
          strtgyallocbrkdwnname
          strtgyallocbrkdwnnamestd
          strtgyallocbrkdwnpct
          tgtallocbrkdwnmin
          tgtallocbrkdwnmax
          tgtallocbrkdwnrange
          contribytd
          contrib1mo
          contrib3mo
          contrib1yr
          rank
          frequency
        }
      }
    }
  }
`;

class DistributionHistoryService extends GraphQLDataService {
    constructor(apollo, mapperFactory) {
        super(apollo);
        this.mapperFactory = mapperFactory;
    }
    /**
     * Fetch the distribution history response and map the raw data to the expected model.
     * @param query query to be hit on graphql url
     * @param fundDataServiceParams query variables to be passed
     */
    register(query, fundDataServiceParams) {
        return super.register(query, this.getVariables(fundDataServiceParams)).pipe(map((rawHistoricalData) => {
            var _a, _b;
            const distributionHistoriesDTO = ((_b = (_a = rawHistoricalData === null || rawHistoricalData === void 0 ? void 0 : rawHistoricalData.data) === null || _a === void 0 ? void 0 : _a.DistributionHistory) === null || _b === void 0 ? void 0 : _b.distribution)
                ? rawHistoricalData.data.DistributionHistory.distribution
                : null;
            const mapperParams = {};
            return distributionHistoriesDTO
                ? this.mapperFactory
                    .createMapper(DistributionHistoriesMapper, mapperParams)
                    .toDomain(distributionHistoriesDTO)
                : null;
        }));
    }
    /**
     * Return required variables value to graphql query.
     * @param fundDataServiceParams params required for graphql query
     */
    getVariables(dataServiceParams) {
        return {
            countrycode: this.getCountry(),
            languagecode: this.getLanguage(),
            fundid: dataServiceParams.fundId,
            shareclasscode: dataServiceParams.shareClassCode,
        };
    }
}
DistributionHistoryService.ɵfac = function DistributionHistoryService_Factory(t) { return new (t || DistributionHistoryService)(i0.ɵɵinject(i1.Apollo), i0.ɵɵinject(MapperFactory)); };
DistributionHistoryService.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: DistributionHistoryService, factory: DistributionHistoryService.ɵfac, providedIn: 'root' });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DistributionHistoryService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: i1.Apollo }, { type: MapperFactory }]; }, null); })();

class ChartsService extends GraphQLDataService {
    constructor(apollo, mapperFactory) {
        super(apollo);
        this.mapperFactory = mapperFactory;
    }
    /**
     * Fetch the charts response and map the raw data to the expected model.
     * @param query query to be hit on graphql url
     * @param fundDataServiceParams query variables to be passed
     */
    register(query, fundDataServiceParams) {
        return super.register(query, this.getVariables(fundDataServiceParams)).pipe(map((rawHistoricalData) => {
            var _a, _b;
            const chartsDTO = ((_b = (_a = rawHistoricalData === null || rawHistoricalData === void 0 ? void 0 : rawHistoricalData.data) === null || _a === void 0 ? void 0 : _a.TimeSeries) === null || _b === void 0 ? void 0 : _b.charts)
                ? rawHistoricalData.data.TimeSeries.charts
                : null;
            const mapperParams = {};
            return chartsDTO
                ? this.mapperFactory
                    .createMapper(ChartsMapper, mapperParams)
                    .toDomain(chartsDTO)
                : null;
        }));
    }
    /**
     * Return required variables value to graphql query.
     * @param fundDataServiceParams params required for graphql query
     */
    getVariables(dataServiceParams) {
        return {
            countrycode: this.getCountry(),
            languagecode: this.getLanguage(),
            fundid: dataServiceParams.fundId,
            shareclasscode: dataServiceParams.shareClassCode,
        };
    }
}
ChartsService.ɵfac = function ChartsService_Factory(t) { return new (t || ChartsService)(i0.ɵɵinject(i1.Apollo), i0.ɵɵinject(MapperFactory)); };
ChartsService.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: ChartsService, factory: ChartsService.ɵfac, providedIn: 'root' });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ChartsService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: i1.Apollo }, { type: MapperFactory }]; }, null); })();

class PortfolioService extends GraphQLDataService {
    constructor(apollo, mapperFactory) {
        super(apollo);
        this.mapperFactory = mapperFactory;
    }
    /**
     * Fetch the Portfolio response and map the raw data to the expected model.
     * @param query query to be hit on graphql url
     * @param fundDataServiceParams query variables to be passed
     */
    register(query, fundDataServiceParams) {
        return super.register(query, this.getVariables(fundDataServiceParams)).pipe(map((rawPortfolioData) => {
            var _a;
            const portfolioDTO = ((_a = rawPortfolioData === null || rawPortfolioData === void 0 ? void 0 : rawPortfolioData.data) === null || _a === void 0 ? void 0 : _a.Portfolio) || null;
            const mapperParams = {};
            return portfolioDTO
                ? this.mapperFactory
                    .createMapper(PortfolioMapper, mapperParams)
                    .toDomain(portfolioDTO)
                : null;
        }));
    }
    /**
     * Return required variables value to graphql query.
     * @param fundDataServiceParams params required for graphql query
     */
    getVariables(dataServiceParams) {
        return {
            countrycode: this.getCountry(),
            languagecode: this.getLanguage(),
            fundid: dataServiceParams.fundId,
        };
    }
}
PortfolioService.ɵfac = function PortfolioService_Factory(t) { return new (t || PortfolioService)(i0.ɵɵinject(i1.Apollo), i0.ɵɵinject(MapperFactory)); };
PortfolioService.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: PortfolioService, factory: PortfolioService.ɵfac, providedIn: 'root' });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PortfolioService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: i1.Apollo }, { type: MapperFactory }]; }, null); })();

const logger = Logger.getLogger('ProductSnapshotService');
/**
 *  API URL to fetch product data from.
 */
const API_URL_TEMPLATE = '/ft-alts/assets/alts/data/${fundId}/funds.class.${shareClass}.json';
const API_URL_TEMPLATE_WITHOUT_SHARE_CLASS = '/ft-alts/assets/alts/data/${fundId}/${type}.json';
const API_URL_TEMPLATE_PROD = '/assets/alts/data/${fundId}/funds.class.${shareClass}.json';
const API_URL_TEMPLATE_WITHOUT_SHARE_CLASS_PROD = '/assets/alts/data/${fundId}/${type}.json';
/**
 * Service for managing product data.
 * @class ProductSnapshotService
 */
class ProductSnapshotService {
    /**
     * Creates an instance of ProductSnapshotService.
     * @param productOverviewService - pull Overview data
     * @param productDetailsService - pull Product Detail data
     * @param distributionHistoryService - pull Distribution History data
     * @param chartsService - pull Charts data
     * @param productPortfolioService - pull Portfolio data
     * @param http - to pull json data
     * @memberof ProductSnapshotService
     */
    constructor(productOverviewService, productDetailsService, distributionHistoryService, chartsService, productPortfolioService, portfolioDetailsService, http) {
        this.productOverviewService = productOverviewService;
        this.productDetailsService = productDetailsService;
        this.distributionHistoryService = distributionHistoryService;
        this.chartsService = chartsService;
        this.productPortfolioService = productPortfolioService;
        this.portfolioDetailsService = portfolioDetailsService;
        this.http = http;
        this.productData$ = new Subject();
        this.productNAVData$ = new Subject();
        this.portfolioData$ = new Subject();
        this.productOverview$ = new Subject();
        this.productDetails$ = new Subject();
        this.distributionHistory$ = new Subject();
        this.charts$ = new Subject();
        this.productPortfolio$ = new Subject();
    }
    /**
     * ************************************************************
     * ************* OLD STATIC IMPLEMENTATION STARTS *************
     * *********** TODO: BELOW CODE TO BE REMOVED LATER - be careful do not remove everything ***********
     * ************************************************************
     */
    /**
     * Returns an observable subject that emits product data whenever it is updated.
     * @returns {Observable<ProductData>} An observable subject for product data updates
     * @memberof ProductSnapshotService
     */
    getProductData$() {
        return this.productData$.asObservable();
    }
    /**
     * Returns an observable subject that emits portfolio data whenever it is updated.
     * @returns {Observable<Portfolio>} An observable subject for product data updates
     * @memberof ProductSnapshotService
     */
    getPortfolioData$() {
        return this.portfolioData$.asObservable();
    }
    /**
     * Fetches product data from the specified API URL and notifies subscribers.
     * @memberof ProductSnapshotService
     */
    populateProductData$(fundId, shareClass) {
        // TODO: Call below from product-hero directly when above is removed.
        // this.populateProductSnapshot(
        //  fundId as FundId,
        //  shareClass as ShareClassCode
        // );
        const apiUrl = this.getApiUrl(fundId, shareClass);
        return (this.http
            .get(apiUrl === null || apiUrl === void 0 ? void 0 : apiUrl.toLowerCase())
            // Assuming your API response has a property named 'data'
            .pipe(map((response) => response.data), tap((data) => logger.debug('Fetched product data: ', data))));
    }
    /**
     * Fetches product data from the specified API URL and notifies subscribers.
     * @memberof ProductSnapshotService
     */
    populatePortfolioData(fundId) {
        switch (fundId) {
            case '92083': {
                this.populatePortfolio(fundId, PortfolioCPREX);
                break;
            }
            case '29429': {
                this.populatePortfolio(fundId, PortfolioBSP);
                break;
            }
            case '18120': {
                this.populatePortfolio(fundId, PortfolioK2);
                break;
            }
            case '90022': {
                this.populatePortfolio(fundId, PortfolioBSP);
                break;
            }
        }
    }
    /**
     * Fetches product data from the specified API URL and notifies subscribers.
     * @memberof ProductSnapshotService
     */
    populatePortfolioDataFromBR(data) {
        this.portfolioData$.next(data);
    }
    /**
     * Returns a formatted apiUrl with the tokens replaced
     * @param fundId {string} - The fund id
     * @param shareClass {string} - The shareclass
     * @returns {string} A apiUrl with the tokens replaced
     */
    getApiUrl(fundId, shareClass, type) {
        // REMOVE AFTER PDS Integration
        if ((shareClass === null || shareClass === void 0 ? void 0 : shareClass.toLowerCase()) === 'z') {
            shareClass = 'advisor';
        }
        // Use string interpolation to replace tokens with actual values
        if (!shareClass) {
            // HORRIBLE HACK - READ DEPLOYURL
            if (window.location.href.indexOf('alternativesbyft.com') > -1) {
                return API_URL_TEMPLATE_WITHOUT_SHARE_CLASS_PROD.replace('${fundId}', fundId).replace('${type}', type === null || type === void 0 ? void 0 : type.toLowerCase());
            }
            return API_URL_TEMPLATE_WITHOUT_SHARE_CLASS.replace('${fundId}', fundId)
                .replace('${type}', type === null || type === void 0 ? void 0 : type.toLowerCase())
                .toLowerCase();
        }
        // HORRIBLE HACK - READ DEPLOYURL
        if (window.location.href.indexOf('alternativesbyft.com') > -1) {
            return API_URL_TEMPLATE_PROD.replace('${fundId}', fundId).replace('${shareClass}', shareClass === null || shareClass === void 0 ? void 0 : shareClass.replace(/\s+/g, '-').toLowerCase());
        }
        return API_URL_TEMPLATE.replace('${fundId}', fundId).replace('${shareClass}', shareClass === null || shareClass === void 0 ? void 0 : shareClass.replace(/\s+/g, '-').toLowerCase());
    }
    /**
     * ************************************************************
     * ***************** PDS IMPLEMENTATION STARTS ****************
     * ************************************************************
     */
    getProductNAVData$() {
        return this.productNAVData$.asObservable();
    }
    /**
     * Returns an observable that emits product overview whenever it is updated.
     */
    getProductOverview$() {
        return this.productOverview$.asObservable();
    }
    /**
     * Returns an observable that emits product details whenever it is updated.
     */
    getProductDetails$() {
        return this.productDetails$.asObservable();
    }
    /**
     * Returns an observable that emits distribution history whenever it is updated.
     */
    getDistributionHistory$() {
        return this.distributionHistory$.asObservable();
    }
    /**
     * Returns an observable that emits charts data whenever it is updated.
     */
    getCharts$() {
        return this.charts$.asObservable();
    }
    /**
     * Returns an observable that emits Portfolio data whenever it is updated.
     */
    getProductPortfolio$() {
        return this.productPortfolio$.asObservable();
    }
    /**
     * Fetches all product detail required in one go.
     * @param fundId current loaded product fund id
     * @param shareClassCode current loaded product share class code
     */
    populateProductSnapshot(fundId, shareClass) {
        logger.debug('rxjs: populateProductSnapshot called');
        const shareClassCode = this.formatShareClassCode(fundId, shareClass);
        forkJoin([
            // Register Product Details
            this.productDetailsService.register(ProductDetailsQuery, {
                fundId,
            }),
            // Register Product Overview
            this.productOverviewService.register(ProductOverviewQuery, {
                fundId,
                shareClassCode,
            }),
            // Register Distribution Service
            this.distributionHistoryService.register(DistributionHistoryQuery, {
                fundId,
                shareClassCode,
            }),
            // Register to Charts
            this.chartsService.register(ChartsQuery, {
                fundId,
                shareClassCode,
            }),
            // Register Portfolio
            this.productPortfolioService.register(PortfolioQuery, {
                fundId,
            }),
            // this.populateProductData$(fundId, shareClassCode),
        ]).subscribe({
            next: (combinedResult) => {
                logger.debug('rxjs: populateProductSnapshot fork joined ', combinedResult);
                this.productDetails$.next(combinedResult[0]);
                this.productOverview$.next(combinedResult[1]);
                this.distributionHistory$.next(combinedResult[2]);
                this.charts$.next(combinedResult[3]);
                this.productPortfolio$.next(combinedResult[4]);
                // this.productNAVData$.next(combinedResult[5]?.nav);
            },
            error: (error) => {
                logger.error('Error in fetching combined product snapshot: ', error);
            },
        });
    }
    formatShareClassCode(fundId, shareClassCode) {
        if ((fundId === null || fundId === void 0 ? void 0 : fundId.toLowerCase()) === 'cofdascore' ||
            (fundId === null || fundId === void 0 ? void 0 : fundId.toLowerCase()) === 'cofdascorecap' ||
            (fundId === null || fundId === void 0 ? void 0 : fundId.toLowerCase()) === 'cofdasdynamic') {
            fundId = fundId === null || fundId === void 0 ? void 0 : fundId.toUpperCase();
            // for SMA's API call
            if (shareClassCode.toLowerCase() === 'na') {
                shareClassCode = 'NA';
            }
        }
        shareClassCode = shareClassCode === null || shareClassCode === void 0 ? void 0 : shareClassCode.toUpperCase();
        return shareClassCode;
    }
    /**
     * Fetches all product detail required in one go.
     * @param fundId current loaded product fund id
     * @param shareClassCode current loaded product share class code
     */
    populatePortfolio(fundId, query) {
        this.portfolioDetailsService
            .register(query, {
            fundId,
        })
            .subscribe({
            next: (result) => {
                this.portfolioData$.next(result);
            },
            error: (error) => {
                logger.error('Error in fetching combined product snapshot: ', error);
            },
        });
    }
}
ProductSnapshotService.ɵfac = function ProductSnapshotService_Factory(t) { return new (t || ProductSnapshotService)(i0.ɵɵinject(ProductOverviewService), i0.ɵɵinject(ProductDetailsService), i0.ɵɵinject(DistributionHistoryService), i0.ɵɵinject(ChartsService), i0.ɵɵinject(PortfolioService), i0.ɵɵinject(PortfolioDetailsService), i0.ɵɵinject(i7.HttpClient)); };
ProductSnapshotService.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: ProductSnapshotService, factory: ProductSnapshotService.ɵfac, providedIn: 'root' });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ProductSnapshotService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ProductOverviewService }, { type: ProductDetailsService }, { type: DistributionHistoryService }, { type: ChartsService }, { type: PortfolioService }, { type: PortfolioDetailsService }, { type: i7.HttpClient }]; }, null); })();

// Services

var ConfigurationId;
(function (ConfigurationId) {
    ConfigurationId["GW"] = "GW";
    ConfigurationId["ETF"] = "ETFs";
    ConfigurationId["CEF"] = "Closed-End Funds";
    ConfigurationId["MF"] = "Mutual Funds";
    ConfigurationId["MMF"] = "Money Market Funds";
    ConfigurationId["TIF"] = "TIF";
    ConfigurationId["VIP"] = "Variable Insurance";
    ConfigurationId["_529"] = "529 Portfolios";
    ConfigurationId["SMA"] = "Managed Accounts";
    ConfigurationId["IF"] = "Interval Funds";
    ConfigurationId["US"] = "US";
    ConfigurationId["TOF"] = "Tender Offer Funds";
})(ConfigurationId || (ConfigurationId = {}));

var CurrencyCode;
(function (CurrencyCode) {
    CurrencyCode["AUD"] = "AUD";
    CurrencyCode["CAD"] = "CAD";
    CurrencyCode["CHF"] = "CHF";
    CurrencyCode["CNH"] = "CNH";
    CurrencyCode["CZK"] = "CZK";
    CurrencyCode["EUR"] = "EUR";
    CurrencyCode["GBP"] = "GBP";
    CurrencyCode["HKD"] = "HKD";
    CurrencyCode["HUF"] = "HUF";
    CurrencyCode["JPY"] = "JPY";
    CurrencyCode["NOK"] = "NOK";
    CurrencyCode["PLN"] = "PLN";
    CurrencyCode["SEK"] = "SEK";
    CurrencyCode["SGD"] = "SGD";
    CurrencyCode["USD"] = "USD";
    CurrencyCode["ZAR"] = "ZAR";
    CurrencyCode["INR"] = "INR";
})(CurrencyCode || (CurrencyCode = {}));

var ProductTypes;
(function (ProductTypes) {
    ProductTypes["TIF"] = "TIF";
    ProductTypes["MUTUAL_FUNDS"] = "Mutual Funds";
    ProductTypes["VARIABLE_INSURANCE"] = "Variable Insurance";
    ProductTypes["ETFs"] = "ETFs";
    ProductTypes["CLOSED_END_FUNDS"] = "Closed-End Funds";
    ProductTypes["MONEY_MARKET_FUNDS"] = "Money Market Funds";
    ProductTypes["MANAGED_ACCOUNTS"] = "Managed Accounts";
    ProductTypes["INDICES"] = "Indices";
    ProductTypes["529_PORTFOLIOS"] = "529 Portfolios";
    ProductTypes["SEGREGATED"] = "Segregated";
    ProductTypes["INTERVAL_FUNDS"] = "Interval Funds";
    ProductTypes["TENDER_OFFER_FUNDS"] = "Tender Offer Funds";
})(ProductTypes || (ProductTypes = {}));

// NOTE: Any new value declared here should match PDS strictly.
var CalcTypeStd;
(function (CalcTypeStd) {
    CalcTypeStd["NAV"] = "NAV";
    CalcTypeStd["NET"] = "NET";
    CalcTypeStd["POP"] = "POP";
    CalcTypeStd["MARKET_PRICE"] = "MARKET_PRICE";
    CalcTypeStd["GROSS"] = "GROSS";
    CalcTypeStd["PUREGROSS"] = "PUREGROSS";
})(CalcTypeStd || (CalcTypeStd = {}));

/**
 * Generated bundle index. Do not edit.
 */

export { BMPerformanceCurrentMapper, BMPerformanceMapper, BenchmarkCumulativePerformanceHistoryCategoryMapper, BenchmarkCumulativePerformanceHistoryMapper, CalcTypeStd, ChartTypeMapper, ChartsMapper, ConfigurationId, CumulativePerformanceHistoryCategoryMapper, CumulativePerformanceHistoryTimePeriodMapper, CurrencyCode, DistributionHistoriesMapper, DistributionHistoryMapper, DistributionMapper, DistributionsMapper, FundCumulativePerformanceHistoryMapper, GraphQLDataService, IdentifiersMapper, LabelLoader, LabelsService, Mapper, MapperFactory, NavMapper, PerformanceCurrentMapper, PerformanceMapper, PortfolioDetailsService, PortfolioMapper, ProductDetailsService, ProductMapper, ProductOverviewService, ProductSnapshotService, ProductTypeParameter, ProductTypes, RiskStatsSummariesMapper, RiskStatsSummaryMapper, SalesChargeBreakpointMapper, SalesChargeBreakpointsMapper, ShareClassMapper, ShareClassesMapper };
